import { useApi } from '@backstage/core-plugin-api';
import { notificationApiRef } from '../../../apis/notificationApi';
import { toolsProvisioningApiRef } from '../../../apis/toolsProvisioningApi';
import { useState } from 'react';

import { QeScoreAnalysisResults } from '../components/qescore/interfaces';
import { mapQeScoreAnalysisResponse } from '../components/qescore/utils/mappers';

type ExecutionLogEvent =
  QeScoreAnalysisResults['executionLogs']['events'][number];

interface RawSseEvent {
  event?: string;
  timestamp?: string;
  crew?: string;
  task?: string;
  agent?: string;
  message?: string;
}

function buildEventKey(e: ExecutionLogEvent): string {
  return `${e.timestamp}|${e.event}|${e.crew ?? ''}|${e.task ?? ''}|${
    e.agent ?? ''
  }`;
}

function mergeExecutionLogEvents(
  existingEvents: ExecutionLogEvent[] = [],
  incomingEvents: ExecutionLogEvent[] = [],
): ExecutionLogEvent[] {
  if (!existingEvents.length) return incomingEvents;
  if (!incomingEvents.length) return existingEvents;

  const eventMap = new Map<string, ExecutionLogEvent>();

  [...existingEvents, ...incomingEvents].forEach(event => {
    eventMap.set(buildEventKey(event), event);
  });

  return Array.from(eventMap.values());
}

function downloadBlobFile(
  blob: Blob,
  fileName: string,
  emptyFileErrorMessage: string,
): void {
  if (!blob.size) {
    throw new Error(emptyFileErrorMessage);
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function useQeScoreAnalysis(apmNumber: string, requestId: string) {
  const notificationApi = useApi(notificationApiRef);
  const toolsprovisioningApi = useApi(toolsProvisioningApiRef);

  const [qeScoreAnalysisResults, setQeScoreAnalysisResults] =
    useState<QeScoreAnalysisResults | null>(null);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);

  const initializeAnalysisResultsForStream = () => {
    setQeScoreAnalysisResults({
      reportMetadata: {
        title: '',
        application: '',
        apmNumber,
        period: '',
        generatedOn: '',
        improvementPotential: 0,
      },
      totalWorkItems: 0,
      highPriority: 0,
      mediumPriority: 0,
      lowPriority: 0,
      actionItems: [],
      executionLogs: {
        apmNumber,
        executionDate: requestId,
        events: [],
      },
    });
  };

  const appendEvent = (rawEvent: RawSseEvent) => {
    const eventText = rawEvent?.event;

    if (!eventText) {
      return;
    }

    const parsedEvent: ExecutionLogEvent = {
      event: `${eventText}`,
      timestamp: rawEvent?.timestamp ?? '',
      crew: rawEvent?.crew,
      task: rawEvent?.task,
      agent: rawEvent?.agent,
      message: rawEvent?.message,
    };

    setQeScoreAnalysisResults(prev => {
      if (!prev) {
        return prev;
      }

      const existingEvents = prev.executionLogs?.events ?? [];
      const updatedEvents = mergeExecutionLogEvents(existingEvents, [
        parsedEvent,
      ]);

      if (updatedEvents.length === existingEvents.length) {
        return prev;
      }

      return {
        ...prev,
        executionLogs: {
          ...prev.executionLogs,
          apmNumber,
          executionDate: requestId,
          events: updatedEvents,
        },
      };
    });
  };

  /** Initializes analysis state from API response  */
  const setQeScoreAnalysisDataFromApi = (rawData: unknown) => {
    setQeScoreAnalysisResults(mapQeScoreAnalysisResponse(rawData));
  };

  /**
   * Retrieves QE Score Analysis for the given APM number.
   * - preserveStreamedLogs=true: keep SSE logs captured during current run and merge with fetched payload.
   * - preserveStreamedLogs=false: replace analysis state with fetched payload (all-at-once population).
   */
  const fetchQeScoreAnalysisResults = async (preserveStreamedLogs = false) => {
    setIsAnalysisLoading(true);
    if (toolsprovisioningApi) {
      try {
        const response = await toolsprovisioningApi.getQeScoreAnalysis(
          apmNumber,
          requestId,
        );

        if (response) {
          if (response.error) {
            throw new Error(response.error);
          }

          const mappedResults = mapQeScoreAnalysisResponse(response);

          setQeScoreAnalysisResults(prev => {
            if (!preserveStreamedLogs || !prev) {
              return mappedResults;
            }

            return {
              ...mappedResults,
              executionLogs: {
                ...mappedResults.executionLogs,
                apmNumber:
                  mappedResults.executionLogs.apmNumber ||
                  prev.executionLogs.apmNumber,
                executionDate:
                  mappedResults.executionLogs.executionDate ||
                  prev.executionLogs.executionDate,
                events: mergeExecutionLogEvents(
                  prev.executionLogs.events,
                  mappedResults.executionLogs.events,
                ),
              },
            };
          });

          return response;
        }
      } catch (err) {
        notificationApi.sendNotification({
          message: `Error occurred - ${err}`,
          disapperAfterMs: 4000,
          severity: 'error',
        });
        // throw err;
      } finally {
        setIsAnalysisLoading(false);
      }
    }

    return null;
  };

  /** Downloads the QE Score Analysis report for the given APM number */
  const fetchQeScoreAnalysisReport = async () => {
    setIsDownloadingReport(true);

    if (toolsprovisioningApi) {
      try {
        const response = await toolsprovisioningApi.getQeScoreAnalysisReport(
          apmNumber,
          qeScoreAnalysisResults?.reportMetadata.generatedOn ?? requestId,
        );

        if (response) {
          const blob =
            response instanceof Blob
              ? response
              : new Blob([response], { type: 'application/pdf' });

          if (!blob.size) {
            throw new Error('Received an empty report file.');
          }

          if (
            blob.type &&
            !blob.type.includes('pdf') &&
            blob.type !== 'application/octet-stream'
          ) {
            throw new Error(
              `Unexpected report format received: ${blob.type}. Please try again later.`,
            );
          }

          downloadBlobFile(
            blob,
            `QE_Score_Analysis_Report_${apmNumber}_${
              qeScoreAnalysisResults?.reportMetadata.generatedOn ?? requestId
            }.pdf`,
            'Received an empty report file.',
          );
        }
      } catch (err) {
        notificationApi.sendNotification({
          message: `Error occurred - ${err}`,
          disapperAfterMs: 4000,
          severity: 'error',
        });
        //throw err;
      } finally {
        setIsDownloadingReport(false);
      }
    }
    return null;
  };

  return {
    qeScoreAnalysisResults,
    isAnalysisLoading,
    isDownloadingReport,
    fetchQeScoreAnalysisResults,
    fetchQeScoreAnalysisReport,
    initializeAnalysisResultsForStream,
    setQeScoreAnalysisDataFromApi,
    appendEvent,
  };
}
